import { IColumn } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPSpinner, IPText, IPTooltipHost } from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import type { TFunction } from 'i18next';
import { isAuditCriteriaPreviewEnabled } from 'models/copilot/copilotPreviewFeatureToggles';
import { IPeriodFilterData } from 'components/Filter/PeriodFilter';
import {
  AuditCriteriaPeriod,
  AuditCriteriaResultFilter,
  AuditCriteriaValue,
  AuditCriteriaValues,
} from 'models/auditCriteriaValues';
import { EntityTypes } from 'models/entity';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiQueryAuditCriteriaValues } from 'services/Api/auditCriteriaService';
import { apiRequest } from 'services/Auth/authConfig';
import { getLocalStorageData, LocalStorageKeys, removeLocalStorageData } from 'utils/localstorage';
import { toAuditCriteriaPeriod } from './criteriaPeriod';
import CriteriaValueCell from './CriteriaValueCell';

// PLAN-075 (spec 9.1, 9.4): the criteria columns of a list. The keys of the columns carry this prefix and the lineage key.
export const criteriaColumnKeyPrefix = 'criteria:';

export const isCriteriaColumnKey = (key: string | undefined): boolean =>
  !!key && key.startsWith(criteriaColumnKeyPrefix);

const lineageKeyOf = (columnKey: string): string => columnKey.substring(criteriaColumnKeyPrefix.length);

// the fallback header stays under this length, the ellipsis included
const columnTitleMaxLength = 40;

/**
 * The short text of a criterion as a column (PLAN-CTI-082): its column name, else its requirement cut at the last word
 * boundary with an ellipsis. The header, the filter entry and the column chooser all read it; the header's tooltip
 * carries the full requirement.
 */
export const getCriterionColumnTitle = (criterion: { columnName: string; requirement: string }): string => {
  const columnName = criterion.columnName.trim();
  if (columnName) return columnName;

  const requirement = criterion.requirement.trim();
  if (requirement.length < columnTitleMaxLength) return requirement;

  const cut = requirement.substring(0, columnTitleMaxLength - 4);
  const lastSpace = cut.lastIndexOf(' ');
  const words = (lastSpace > 0 ? cut.substring(0, lastSpace) : cut).replace(/[\s,;:.-]+$/, '');

  return `${words}...`;
};

export interface ICriteriaResultFilterValue {
  lineageKey: string;
  result: AuditCriteriaResultFilter;
}

// PLAN-075 Task 30: a register's criteria filter is part of its own filter. An entry is a filter value of the group
// criteria$, "criteria$<result>:<lineage key>": it is a quick-filter chip, counts in "Filter (n)" and goes with "Reset
// quick filters" like any other entry. A register's own filter function passes over the group it does not know.
export const criteriaFilterGroupKey = 'criteria$';

export const criteriaFilterResults = [
  AuditCriteriaResultFilter.Pass,
  AuditCriteriaResultFilter.Fail,
  AuditCriteriaResultFilter.None,
];

export const getCriteriaFilterValue = (lineageKey: string, result: AuditCriteriaResultFilter): string =>
  `${criteriaFilterGroupKey}${result}:${lineageKey}`;

export const parseCriteriaFilterValue = (filter: string): ICriteriaResultFilterValue | undefined => {
  if (!filter.startsWith(criteriaFilterGroupKey)) return undefined;

  const value = filter.substring(criteriaFilterGroupKey.length);
  const separator = value.indexOf(':');
  if (separator < 0) return undefined;

  const result = criteriaFilterResults.find((item) => item === value.substring(0, separator));
  if (!result) return undefined;

  return { lineageKey: value.substring(separator + 1), result: result };
};

// "<criterion>: Pass", "...: Fail", "...: Not assessed": an entry in the filter panel and its quick-filter chip
export const getCriteriaFilterEntryText = (
  criterion: string,
  result: AuditCriteriaResultFilter,
  t: TFunction<string[]>,
): string => {
  const resultText =
    result === AuditCriteriaResultFilter.Pass
      ? t('criteria:Columns.Filter.Pass')
      : result === AuditCriteriaResultFilter.Fail
      ? t('criteria:Columns.Filter.Fail')
      : t('criteria:Columns.Filter.None');

  return t('criteria:Columns.Filter.Entry', { criterion: criterion, result: resultText });
};

export interface ICriteriaColumns<ItemType> {
  // the columns, off by default: the column chooser turns them on (data.defaultHidden)
  columns: IColumn[];
  values: AuditCriteriaValues;
  isLoading: boolean;
  // the first values read for the register's items is done: until then a criteria chip cannot be named
  isLoaded: boolean;
  // the PLAN-075 preview (isAuditCriteriaPreviewEnabled)
  enabled: boolean;
  period: AuditCriteriaPeriod;
  setPeriod: (period: AuditCriteriaPeriod) => void;
  // the items that pass the criteria entries of the register's filter (all of them without one): an item passes
  // when, for every criterion with entries, its value matches one of them
  applyResultFilter: (items: ItemType[]) => ItemType[];
  // for a criteria column key: fail before pass, then the older answer first; an item without an answer is last either way
  compare: (a: ItemType, b: ItemType, columnKey: string, isSortedDescending?: boolean) => number;
  sortItems: (items: ItemType[], column: IColumn) => ItemType[];
  // the quick-filter chip of a criteria entry; undefined for a filter of another group, which the register's own
  // renderer renders (choose by that, never by a null: the two renderers call different hooks). A small spinner
  // until the values are read, because a chip that renders null is removed.
  renderQuickFilter: (filter: string) => JSX.Element | null | undefined;
}

// PeriodFilter's stored choice; undefined when there is none or it is not PeriodFilter's (the old dropdown's
// { kind, from, to }, which PeriodFilter would read as an audit year)
const readStoredPeriod = (stored: string | undefined): IPeriodFilterData | undefined => {
  if (!stored) return undefined;

  try {
    const parsed = JSON.parse(stored) as IPeriodFilterData;

    return parsed && typeof parsed === 'object' && parsed.option ? parsed : undefined;
  } catch {
    return undefined;
  }
};

const resultRank = (value: AuditCriteriaValue): number => {
  if (value.isFail()) return 0;
  if (value.isPass()) return 1;

  return 2;
};

/**
 * The criteria columns of a register: one POST criteria/values/query per register load and per period change, for the
 * loaded ids. A column's title is the criterion's column title only (getCriterionColumnTitle), also for a criterion of a
 * subtype set; selectedFilter is the register's filter, whose criteria entries applyResultFilter applies.
 */
const useCriteriaColumns = <ItemType,>(
  entityType: EntityTypes,
  items: ItemType[] | undefined,
  getId: (item: ItemType) => number,
  periodStorageKey: LocalStorageKeys,
  selectedFilter: string[],
): ICriteriaColumns<ItemType> => {
  const { t } = useTranslation(['criteria']);
  const appContext = useContext(AppContext);
  const [values, setValues] = useState<AuditCriteriaValues>(new AuditCriteriaValues());
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  // the register's PeriodFilter persists the choice under periodStorageKey; the first period is read from it, so the
  // first load asks for the stored period. A value it cannot read is removed, so PeriodFilter starts on its default too
  const [period, setPeriodState] = useState<AuditCriteriaPeriod>(() => {
    const stored = getLocalStorageData(appContext, periodStorageKey);
    const data = readStoredPeriod(stored);
    if (stored && !data) removeLocalStorageData(appContext, periodStorageKey);

    return data ? toAuditCriteriaPeriod(data, appContext) : new AuditCriteriaPeriod();
  });
  // the load reads the context through a ref: a new context value must not reload the values
  const appContextRef = useRef(appContext);
  appContextRef.current = appContext;

  // the ids as one string: the items array changes identity on every render of a list, the loaded ids do not
  const idsKey = useMemo(() => (items ?? []).map(getId).join(','), [items, getId]);
  const enabled = isAuditCriteriaPreviewEnabled(appContext);

  useEffect(() => {
    let current = true;

    const load = async () => {
      const ids = idsKey ? idsKey.split(',').map(Number) : [];
      if (ids.length === 0 || !enabled) {
        setValues(new AuditCriteriaValues());

        return;
      }

      try {
        setIsLoading(true);
        const accessToken = await appContextRef.current.getAccessToken(apiRequest.scopes);
        const result = await apiQueryAuditCriteriaValues(entityType, ids, period, accessToken);
        if (current) {
          setValues(result);
          setIsLoaded(true);
        }
      } catch (err) {
        if (current) appContextRef.current.setError(err);
      } finally {
        if (current) setIsLoading(false);
      }
    };

    load();

    return () => {
      current = false;
    };
  }, [entityType, idsKey, period, enabled]);

  // PeriodFilter reports its choice on mount as well: an equal period keeps the current one, so it does not load again
  const setPeriod = useCallback((newPeriod: AuditCriteriaPeriod) => {
    setPeriodState((current) => (current.isEqual(newPeriod) ? current : newPeriod));
  }, []);

  const columns = useMemo((): IColumn[] => {
    return values.columns.map((column) => {
      const title = getCriterionColumnTitle(column);
      const name = column.specific ? t('criteria:Columns.OwnColumn', { requirement: title }) : title;

      return {
        key: criteriaColumnKeyPrefix + column.lineageKey,
        name: name,
        minWidth: 90,
        maxWidth: 160,
        isResizable: true,
        isMultiline: false,
        data: { defaultHidden: true, lineageKey: column.lineageKey },
        // the header is short; its tooltip is the full requirement
        onRenderHeader: (headerProps, defaultRender) => (
          <IPTooltipHost content={column.requirement}>
            {defaultRender ? defaultRender(headerProps) : name}
          </IPTooltipHost>
        ),
        onRender: (item?: ItemType) =>
          item !== undefined ? (
            <CriteriaValueCell
              value={values.get(getId(item), column.lineageKey)}
              outcome={values.getOutcome(getId(item), column.lineageKey)}
            />
          ) : null,
      };
    });
  }, [values, getId, t]);

  const compare = useCallback(
    (a: ItemType, b: ItemType, columnKey: string, isSortedDescending?: boolean): number => {
      const lineageKey = lineageKeyOf(columnKey);
      const left = values.get(getId(a), lineageKey);
      const right = values.get(getId(b), lineageKey);

      // an item without an answer is last in both directions
      if (!left || !right) {
        if (!left && !right) return 0;

        return !left ? 1 : -1;
      }

      const output = resultRank(left) - resultRank(right) || left.resultDate.getTime() - right.resultDate.getTime();

      return isSortedDescending ? -output : output;
    },
    [values, getId],
  );

  const renderQuickFilter = useCallback(
    (filter: string): JSX.Element | null | undefined => {
      if (!filter.startsWith(criteriaFilterGroupKey)) return undefined;
      const entry = parseCriteriaFilterValue(filter);
      if (!entry || !enabled) return null;
      if (!isLoaded) return <IPSpinner size={IPSpinnerSize.small} />;

      const column = columns.find((item) => item.data?.lineageKey === entry.lineageKey);

      return column ? <IPText>{getCriteriaFilterEntryText(column.name, entry.result, t)}</IPText> : null;
    },
    [enabled, isLoaded, columns, t],
  );

  const sortItems = useCallback(
    (sortable: ItemType[], column: IColumn): ItemType[] => {
      return sortable.slice().sort((a, b) => compare(a, b, column.key, column.isSortedDescending));
    },
    [compare],
  );

  // the criteria entries of the register's filter, per criterion; none without the preview
  const filterKey = selectedFilter.filter((filter) => filter.startsWith(criteriaFilterGroupKey)).join(',');
  const resultsByLineage = useMemo(() => {
    const byLineage = new Map<string, AuditCriteriaResultFilter[]>();
    if (!enabled || !filterKey) return byLineage;

    filterKey.split(',').forEach((filter) => {
      const entry = parseCriteriaFilterValue(filter);
      if (entry) byLineage.set(entry.lineageKey, [...(byLineage.get(entry.lineageKey) ?? []), entry.result]);
    });

    return byLineage;
  }, [enabled, filterKey]);

  const applyResultFilter = useCallback(
    (filterable: ItemType[]): ItemType[] => {
      if (resultsByLineage.size === 0) return filterable;

      return filterable.filter((item) =>
        [...resultsByLineage].every(([lineageKey, results]) => {
          const value = values.get(getId(item), lineageKey);

          return results.some((result) => {
            switch (result) {
              case AuditCriteriaResultFilter.Pass:
                return !!value && value.isPass();
              case AuditCriteriaResultFilter.Fail:
                return !!value && value.isFail();
              default:
                return !value;
            }
          });
        }),
      );
    },
    [resultsByLineage, values, getId],
  );

  // one object per change: a tree list rebuilds its hierarchy when the items it gets change identity
  return useMemo(
    () => ({
      columns,
      values,
      isLoading,
      isLoaded,
      enabled,
      period,
      setPeriod,
      applyResultFilter,
      compare,
      sortItems,
      renderQuickFilter,
    }),
    [
      columns,
      values,
      isLoading,
      isLoaded,
      enabled,
      period,
      setPeriod,
      applyResultFilter,
      compare,
      sortItems,
      renderQuickFilter,
    ],
  );
};

export default useCriteriaColumns;
